// Props are forwarded via `props` to helper functions outside the component; ESLint can't trace that.
/* eslint-disable react/no-unused-prop-types */
import PropTypes from "prop-types";
import React from "react";
import { FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Table, Title } from "@yoast/ui-library";
import * as styles from "./styles.module.scss";
import { messages } from "./messages";
import SubscriptionRow from "./SubscriptionRow";
// `CollapsibleSubscriptionsRows` is deliberately no longer imported. Grouping moved out of the client
// because a group's rows can split across pages, which turns one collapsible of 40 into a
// "complete"-looking one of 25 plus one of 15. The file stays for one release so reverting that decision
// is a one-line change rather than an archaeology exercise.

/**
 * Returns the title text for the SubscriptionList component.
 *
 * @param {object} props The props to use.
 * @returns {Element} The title text.
 */
function getTitle( props ) {
	if ( props.needsAttention ) {
		return <FormattedMessage { ...messages.needsAttentionTitle } />;
	}

	if ( props.isProvisioned ) {
		return <FormattedMessage { ...messages.partnerSubscriptionsTitle } />;
	}

	if ( props.isInactive ) {
		return <FormattedMessage { ...messages.inactiveSubscriptionsTitle } />;
	}

	return <FormattedMessage { ...messages.subscriptionsTitle } />;
}

/**
 * Renders one subscription as a row.
 *
 * `SubscriptionRow` still takes a one-element `subscriptionsArray` rather than a subscription: it was
 * built to render either a single subscription or the head of a collapsible group, and reshaping its
 * whole interface is not what this change is about.
 *
 * @param {Object} subscription The subscription to render.
 * @param {Object} props        The table's props.
 *
 * @returns {JSX.Element} The row.
 */
function getTableRow( subscription, props ) {
	return <SubscriptionRow
		key={ subscription.id }
		subscriptionsArray={ [ subscription ] }
		onManage={ props.onManage }
		isProvisioned={ props.isProvisioned }
		needsAttention={ props.needsAttention }
		isInactive={ props.isInactive }
		showDetailsModal={ props.showDetailsModal }
	/>;
}

/**
 *
 * @param {Object} props The props to use
 *
 * @returns {ReactElement} The rendered component.
 */
const SubscriptionsTable = ( props ) => {
	const isPartnerSubscriptionsTable = props.isProvisioned;

	/**
	 * Get the default headers for the table.
	 *
	 * @returns {Element} The default headers.
	 */
	function getDefaultHeaders() {
		return <Table.Head>
			<Table.Row>
				<Table.Header>
					{ props.intl.formatMessage( messages.tableHeaderSubscriptions ) }
				</Table.Header>
				<Table.Header className={ styles.tableUsage } data-label={ messages.tableHeaderUsedOn.defaultMessage }>
					{ props.intl.formatMessage( messages.tableHeaderUsedOn ) }
				</Table.Header>
				<Table.Header className={ styles.tableRenewal } data-label={ messages.tableHeaderBillingType.defaultMessage }>
					{ props.intl.formatMessage( messages.tableHeaderBillingType ) }
				</Table.Header>
				<Table.Header className={ styles.tableBilling } data-label={ messages.tableHeaderNextBilling.defaultMessage }>
					{ props.intl.formatMessage( messages.tableHeaderNextBilling ) }
				</Table.Header>
				<Table.Header>
					<div />
				</Table.Header>
			</Table.Row>
		</Table.Head>;
	}

	function getPartnerSubscriptionsHeaders() {
		return <Table.Head>
			<Table.Row>
				<Table.Header>
					{ props.intl.formatMessage( messages.tableHeaderSubscriptions ) }
				</Table.Header>
				<Table.Header>
					{ props.intl.formatMessage( messages.tableHeaderPartner ) }
				</Table.Header>
				<Table.Header>
					<div />
				</Table.Header>
			</Table.Row>
		</Table.Head>;
	}

	/**
	 * Renders the component.
	 *
	 * @returns {JSX.Element} Component.
	 * */
	return (
		<div>
			{ /* The stacked sections render their own heading, with the account-wide count beside it, so the
				table's own title would be a duplicate. Kept as an option rather than removed because the table
				is still used on its own. */ }
			{ ! props.hideHeading && <>
				<Title as="h2" size="2">
					{ getTitle( props ) }
				</Title>
				<br />
			</> }
			<div className={ styles.tableWrapper }>
				<Table>
					<React.Fragment key="0.1">
						{ isPartnerSubscriptionsTable ? getPartnerSubscriptionsHeaders() : getDefaultHeaders() }
						<Table.Body>
							{ props.subscriptions.map( subscription => getTableRow( subscription, props ) ) }
						</Table.Body>
					</React.Fragment>
				</Table>
			</div>
			<br />
		</div>
	);
};

/**
 * The shape of one row, as the read model ships it.
 *
 * This used to end in a `PropTypes.object` alternative, to admit the object-of-arrays the grouping
 * pipeline produced. That escape hatch matched anything, so it silently switched off validation of every
 * field beside it — including the two `instanceOf( Date )` checks, which is how a string date could reach
 * `SubscriptionDetailModal` and break its comparison without warning. A flat list is what finally makes
 * the shape enforceable, so it is spelled out and the hatch is gone.
 *
 * Nullable fields are typed as such rather than `isRequired`: the server sends null for a subscription
 * with no end date, no provisioner or no renewal URL, and declaring those required would print a warning
 * on perfectly ordinary rows — the fastest way to train everyone to ignore propType output.
 */
export const SubscriptionProps = PropTypes.arrayOf(
	PropTypes.shape(
		{
			id: PropTypes.string.isRequired,
			icon: PropTypes.string,
			name: PropTypes.string.isRequired,
			status: PropTypes.string.isRequired,
			used: PropTypes.number.isRequired,
			limit: PropTypes.number.isRequired,
			usedSeats: PropTypes.number.isRequired,
			subscriptionNumber: PropTypes.string,
			requiresManualRenewal: PropTypes.bool.isRequired,
			hasNextPayment: PropTypes.bool.isRequired,
			nextPayment: PropTypes.instanceOf( Date ),
			hasEndDate: PropTypes.bool.isRequired,
			endDate: PropTypes.instanceOf( Date ),
			billingAmount: PropTypes.number,
			billingCurrency: PropTypes.string,
			provisionerName: PropTypes.string,
			provisionerPlatformUrl: PropTypes.string,
			showManageButton: PropTypes.bool.isRequired,
			isInvitable: PropTypes.bool.isRequired,
			isAssignableToSites: PropTypes.bool.isRequired,
			renewalUrl: PropTypes.string,
		},
	),
);

SubscriptionsTable.propTypes = {
	subscriptions: SubscriptionProps,
	onManage: PropTypes.func.isRequired,
	intl: intlShape.isRequired,
	needsAttention: PropTypes.bool,
	isProvisioned: PropTypes.bool,
	isInactive: PropTypes.bool,
	showDetailsModal: PropTypes.func,
	hideHeading: PropTypes.bool,
};

SubscriptionsTable.defaultProps = {
	subscriptions: [],
	needsAttention: false,
	isProvisioned: false,
	isInactive: false,
	showDetailsModal: null,
	hideHeading: false,
};

export default injectIntl( SubscriptionsTable );
